import { useEffect, useState } from "react";
import {
  withTrackEvent,
  actionNames,
  actionPrefixes,
  objectTypes,
  ActionPrefixValue,
  ActionNameValue,
  ObjectTypeValue,
} from "@newsela/snitch";

import { useRouter } from "next/router";
import useInsightsSharedContext from "@/reports/teacher/utils/useInsightsSharedContext/useInsightsSharedContext";
import getClassroomsSidebarList from "@/reports/teacher/utils/getClassroomsSidebarList";
import getClassroomStudentsList from "@/reports/teacher/utils/getClassroomStudentsList";
import getStudentsSidebarList from "@/reports/teacher/utils/getStudentsSidebarList";
import Link from "@/components/Link/";
import { listNavigationItemCss } from "../SidebarNavigationList.style";
import SidebarSubmenuList from "../SidebarSubmenuList";
import { SidebarItem } from "../SidebarNavigationList";

const LinkWithTrackEvent = withTrackEvent(Link);

export interface LinkTrackingProps {
  objectType: ObjectTypeValue;
  actionPrefix: ActionPrefixValue;
  actionName: ActionNameValue;
  streamProperties: Record<string, any>;
}

interface SidebarNavigationItemProps {
  name: string;
  classroomPageBaseUrl: string;
  streamProperties: LinkTrackingProps["streamProperties"];
  dataQaSelector: string;
  studentPageBaseUrl?: string;
  isStudentPage?: boolean;
  isActive?: boolean;
  displayClassroomList?: boolean;
}

const SidebarNavigationItem = (props: SidebarNavigationItemProps) => {
  const {
    name,
    classroomPageBaseUrl,
    studentPageBaseUrl,
    streamProperties,
    isStudentPage = false,
    dataQaSelector,
    isActive = false,
    displayClassroomList = false,
  } = props;
  const { classrooms, loadingClasses, selectedFilters } =
    useInsightsSharedContext();
  const { studentUniqueId, classroomUniqueId } = selectedFilters;

  const [studentsSidebarList, setStudentsSidebarList] = useState<SidebarItem[]>(
    [],
  );

  const { query } = useRouter();
  const queryParams = new URLSearchParams(query as Record<string, string>);

  queryParams.delete("student");

  if (!isStudentPage) {
    queryParams.delete("classroom");
  }

  const queryString = queryParams.size > 0 ? `&${queryParams.toString()}` : "";

  const linkTrackingProps: LinkTrackingProps = {
    objectType: objectTypes.report,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.navigate,
    streamProperties,
  };

  useEffect(() => {
    if (isStudentPage && !loadingClasses && classroomUniqueId && classrooms) {
      const currentStudentList = getClassroomStudentsList(
        classrooms,
        classroomUniqueId,
      );

      if (currentStudentList) {
        const studentsSidebarList = getStudentsSidebarList(currentStudentList);
        setStudentsSidebarList(studentsSidebarList);
      }
    }
  }, [loadingClasses, classroomUniqueId, classrooms]);

  const shouldRenderClassroomList =
    displayClassroomList && isActive && !isStudentPage;

  const shouldRenderStudentList =
    !!studentPageBaseUrl && isActive && isStudentPage && !!classroomUniqueId;

  return (
    <>
      <li
        css={listNavigationItemCss({
          isActive: false,
          isSubmenuLink: false,
          isBold: isActive,
        })}
      >
        <LinkWithTrackEvent
          href={classroomPageBaseUrl}
          dataProps={{
            "data-qa-selector": dataQaSelector,
          }}
          isInline
          {...linkTrackingProps}
        >
          {name}
        </LinkWithTrackEvent>
      </li>

      {shouldRenderClassroomList && (
        <SidebarSubmenuList
          items={getClassroomsSidebarList(classrooms)}
          activeItemId={classroomUniqueId}
          anchorBaseUrl={`${classroomPageBaseUrl}?classroom=`}
          queryString={queryString}
          linkTrackingProps={linkTrackingProps}
          qaSelector="teacher-report-sidebar-classroom-link"
        />
      )}

      {shouldRenderStudentList && (
        <SidebarSubmenuList
          items={studentsSidebarList}
          activeItemId={studentUniqueId}
          anchorBaseUrl={`${studentPageBaseUrl}?student=`}
          queryString={queryString}
          linkTrackingProps={linkTrackingProps}
          qaSelector="teacher-report-sidebar-student-link"
        />
      )}
    </>
  );
};

export default SidebarNavigationItem;
